import clsx from 'clsx';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { FormError } from '@/core/FormError.atom';
import { IconSelect } from '@/core/IconSelect.molecule';
import { DURATION_TIME_UNITS } from '@/main/app.constants';
import { TextField } from '@seeqdev/qomponents';
import type { DurationTimeUnit, UnitWithLabel } from '@/main/app.constants';

export type ValueWithUnitsItem = { value: number; units: string };

type DisplayOption = { value: string; text: string };

type OnChangeItem = { value: number | string; units: string | undefined; valid: boolean; propName: string };

interface ValueWithUnitsProps {
  onChange: (item: OnChangeItem) => void;
  min: number;
  /** Classnames for `<IconSelect />` */
  selectClassNames?: string;
  defaultValue: ValueWithUnitsItem | undefined;
  propName?: string;
  isValid?: boolean;
  minIsExclusive?: boolean;
  required?: boolean;
  availableUnits?: readonly (DurationTimeUnit | UnitWithLabel)[];
  unitOptionIndex?: number;
  customError?: boolean;
  disabled?: boolean;
  fromFormBuilder?: boolean;
  largeValueInput?: boolean;
  step?: number | string;
  appendToBody?: boolean;
  extraClassNames?: string;
  fullWidth?: boolean;
}

export const ValueWithUnits = (props: ValueWithUnitsProps) => {
  const {
    propName,
    onChange,
    defaultValue,
    min,
    minIsExclusive,
    required,
    availableUnits,
    customError,
    disabled = false,
    largeValueInput = false,
    step = 'any',
    isValid,
    unitOptionIndex,
    fromFormBuilder = false,
    extraClassNames,
    fullWidth = false,
  } = props;

  const [selectedUnit, setSelectedUnit] = useState<DisplayOption | undefined>(undefined);
  const [selectedValue, setSelectedValue] = useState('');
  const [valid, setValid] = useState(true);
  const [displayOptions, setDisplayOptions] = useState<DisplayOption[]>([]);
  const [pristine, setPristine] = useState(true);

  useEffect(() => {
    const availableOptions = availableUnits || DURATION_TIME_UNITS;
    const updatedOptions = _.map(availableOptions, (option: any) => ({
      value: _.isNil(unitOptionIndex) ? option.unit[0] : option.unit[unitOptionIndex],
      text: option.translationKey ? option.translationKey : option.shortLabel,
    }));
    setDisplayOptions(updatedOptions);
    if (defaultValue) {
      // sometimes the backend returns a different unit than we send along when we create a condition so we need to
      // do some extra work to ensure we select the correct unit
      let unit = _.find(updatedOptions, { value: defaultValue.units });
      if (!unit) {
        _.forEach(availableOptions, (option) => {
          if (_.includes(option.unit, defaultValue.units)) {
            unit = _.find(updatedOptions, { value: option.unit[0] });
          }
        });
      }
      setSelectedUnit(unit);

      if ((selectedValue as any) - defaultValue?.value !== 0 || !selectedValue) {
        setSelectedValue(_.trim(defaultValue?.value?.toString()) ?? '');
      }
    }
  }, [defaultValue, availableUnits]);

  const validate = ({ value, unit }: { value: string | number; unit: DisplayOption | undefined }) => {
    if (required || !_.isNil(unit)) {
      if (!_.isNil(min) || !_.isNil(min)) {
        return minIsExclusive ? Number(value) > min : Number(value) >= min;
      } else {
        return true;
      }
    }

    return true;
  };

  const triggerChange = (newValue: string | null, newUnit: DisplayOption | null) => {
    let unit = selectedUnit;
    let value = selectedValue;
    setPristine(false);

    if (!_.isNaN(newValue) && !_.isNil(newValue)) {
      setSelectedValue(newValue);
      value = newValue;
    }

    if (!_.isNil(newUnit)) {
      setSelectedUnit(newUnit);
      unit = newUnit;
    }

    if (_.isFunction(onChange)) {
      const isValid = validate({ value, unit });
      setValid(isValid);

      onChange(
        _.assign({}, {
          value: value !== '' ? _.toNumber(value) : '',
          units: unit?.value,
          valid: isValid,
          propName,
        } as OnChangeItem),
      );
    }
  };

  const renderErrorMessage = () => {
    if (pristine || customError || fromFormBuilder) {
      return null;
    } else if (required && (_.isNaN(selectedValue) || _.isNil(selectedUnit))) {
      return <FormError errorText="FORM.REQUIRED_FIELD" />;
    } else if (!valid) {
      return (
        <FormError
          errorText={minIsExclusive ? 'VALUE_WITH_UNITS.INVALID_MIN_EXCLUSIVE' : 'VALUE_WITH_UNITS.INVALID'}
          errorParameters={{ min }}
        />
      );
    }
  };

  return (
    <>
      <div
        className={clsx('flexColumnContainer specValueWithUnits', extraClassNames, { flexFill: fullWidth })}
        data-testid={propName}>
        <TextField
          extraClassNames={clsx('value-with-units-value-input', { flexFill: largeValueInput })}
          testId="valueWithUnitsInput"
          name="value"
          placeholder=""
          type="number"
          showError={_.isNil(isValid) ? !valid : !isValid}
          step={step}
          value={selectedValue}
          onChange={(e) => {
            e.stopPropagation();
            setSelectedValue(e.target.value);
            triggerChange(e.target.value, null);
          }}
          disabled={disabled}
          inputGroup="left"
        />
        <div className="tw:w-sq-100!">
          <IconSelect
            disabled={disabled}
            name="valueWithUnitsUnits"
            extraClassNames="tw:rounded-none tw:px-0.1 "
            controlClasses="tw:h-8.5"
            value={selectedUnit}
            selectOptions={displayOptions}
            onChange={(val) => triggerChange(null, val as DisplayOption)}
            inputGroup="right"
            showError={!valid}
          />
        </div>
      </div>
      {renderErrorMessage()}
    </>
  );
};
export default ValueWithUnits;
